import { render } from 'engine/index';
import { rpc } from 'src/backend';
import { Table, Td, Th, TrBody, TrHead } from './TableComponents';
import { Button } from './Button';
import type { PlayAndConnectState } from 'shared/modelTypes/play-and-connect';
import { Timer } from './timer';

export function PlayPlayAndConnect({ licenseCode }: { licenseCode: string }) {
	const view: HTMLElement = <div>Will play game {licenseCode}</div>;

	const sessionId = licenseCode;

	const handleStartRound = () => {
		console.log('Sending start-round message...');
		rpc.sendPlayAndConnectMessage
			.query({ sessionId, update: { kind: 'start-round' } })
			.then(() => {
				console.log('start-round message sent successfully');
			})
			.catch((err) => {
				console.error('Failed to send start-round message:', err);
			});
	};

	rpc.onAdminUpdates.subscribe(
		{ sessionId },
		{
			onData(data) {
				if (data.kind === 'update-state') {
					const state = data.state as PlayAndConnectState;
					renderStage(state);
				}
			},
		},
	);

	const renderStage = (state: PlayAndConnectState) => {
		const s = state.stage;
		switch (s) {
			case 'starting':
				render(view, <div class="animate-pulse text-xl text-gray-700">Starting play session</div>);
				break;
			case 'register-players':
				render(
					view,
					<PlayerRegisterView
						onContinue={(numberOfPlayers, numberOfQuestions) => {
							console.log('start the game');
							rpc.sendPlayAndConnectMessage.query({
								sessionId,
								update: { kind: 'start-game', numberOfQuestions, numberOfPlayers },
							});
						}}
						onSyncNumberOfPlayers={(numberOfPlayers) => {
							rpc.sendPlayAndConnectMessage.query({
								sessionId,
								update: { kind: 'sync-number-of-players', numberOfPlayers },
							});
						}}
						state={state}
					/>,
				);
				break;
			case 'play-rounds':
				{
					const questionDone = state.questionCompleted;
					const isFinalQuestionDone = state.roundIndex > state.numberOfQuestions;

					render(
						view,
						<div>
							<h2 class="text-lg font-bold text-gray-900">Vraag {state.roundIndex + 1}</h2>
							<div>
								{isFinalQuestionDone ? (
									<>
										<p>De laatste vraag is afgerond. Druk op de knop om de einduitslag te tonen.</p>
										<button
											class="mt-3 rounded-md bg-theme-600 px-3 py-1 text-theme-50 shadow-sm"
											on:click={handleStartRound}
										>
											Toon Resultaten
										</button>
									</>
								) : questionDone ? (
									<>
										<p>De vorige vraag is afgerond. Druk op de knop om een nieuwe ronde te starten.</p>
										<button
											class="mt-3 rounded-md bg-theme-600 px-3 py-1 text-theme-50 shadow-sm"
											on:click={handleStartRound}
										>
											Start Ronde
										</button>
									</>
								) : (
									<>
										{state.timer?.timerFrom && state.timer?.timerTo && (
											<Timer timerFrom={state.timer.timerFrom} timerTo={state.timer.timerTo} />
										)}

										<p>
											Kies hieronder of je een volgende ronde van dezelfde vraag wilt starten of direct naar de
											volgende vraag wilt springen.
										</p>
										<div class="flex space-x-3">
											<button
												class="mt-3 rounded-md bg-theme-600 px-3 py-1 text-theme-50 shadow-sm"
												on:click={handleStartRound}
											>
												Volgende ronde
											</button>
											<button
												class="mt-3 rounded-md bg-theme-600 px-3 py-1 text-theme-50 shadow-sm"
												on:click={() =>
													rpc.sendPlayAndConnectMessage.query({
														sessionId,
														update: { kind: 'start-next-question' },
													})
												}
											>
												Volgende vraag
											</button>
										</div>
									</>
								)}
							</div>
							<PlayerTable state={state}></PlayerTable>
						</div>,
					);
				}
				break;
			case 'play-ending-game':
				render(
					view,
					<div>
						<h2 class="text-lg font-bold text-gray-900">Laatste ronde</h2>
						{state.showPreface && (
							<div>
								<button
									class="mt-3 rounded-md bg-theme-600 px-3 py-1 text-theme-50 shadow-sm"
									on:click={() => rpc.sendPlayAndConnectMessage.query({ sessionId, update: { kind: 'end-early' } })}
								>
									Ronde eerder stoppen
								</button>
							</div>
						)}

						{!state.showPreface && (
							<div>
								<p>Wacht tot de ronde voorbij is.</p>
								<button
									class="mt-3 rounded-md bg-theme-600 px-3 py-1 text-theme-50 shadow-sm"
									on:click={() => rpc.sendPlayAndConnectMessage.query({ sessionId, update: { kind: 'end-early' } })}
								>
									Eindresultaat tonen
								</button>
							</div>
						)}
						<PlayerTable state={state}></PlayerTable>
					</div>,
				);
				break;
			case 'share-contact':
				render(
					view,
					<div>
						{state.timer?.timerFrom && state.timer?.timerTo && (
							<Timer timerFrom={state.timer.timerFrom} timerTo={state.timer.timerTo} />
						)}

						<h2 class="text-lg font-bold text-gray-900">Vraag naar contactgegevens</h2>
						<div>
							<button
								class="mt-3 rounded-md bg-theme-600 px-3 py-1 text-theme-50 shadow-sm"
								on:click={() => rpc.sendPlayAndConnectMessage.query({ sessionId, update: { kind: 'end-early' } })}
							>
								Ronde eerder stoppen
							</button>
						</div>
						<PlayerTable state={state}></PlayerTable>
					</div>,
				);
				break;

			case 'ending-report':
				render(
					view,
					<div>
						<h2 class="text-lg font-bold text-gray-900">Showing ending report</h2>
						<PlayerTable state={state}></PlayerTable>
					</div>,
				);
				break;
		}
	};

	// render initial state
	(async () => {
		const state = await rpc.getGameState.query({ sessionId });

		renderStage(state);
	})();

	return view;
}

function PlayerRegisterView({
	state,
	onContinue,
	onSyncNumberOfPlayers,
}: {
	state: PlayAndConnectState;
	onContinue: (numberOfPlayers: number, numberOfQuestions: number) => void;
	onSyncNumberOfPlayers: (numberOfPlayers: number) => void;
}) {
	const playersWithName = state.players.filter((p) => p.name);

	const currentNumberOfQuestions = state.numberOfQuestions || state.questions.length || 5;
	const currentNumberOfPlayers = state.numberOfPlayers || 6;

	return (
		<div>
			<h3 class="text-lg font-bold text-gray-800 dark:text-gray-200">Spelers registreren</h3>
			<p class="mb-5 text-gray-700">
				Laat spelers de QR code op het bord scannen en vervolgens hun gegevens invullen.
				<br />
				Druk op <strong>doorgaan</strong> als alle spelers gereed zijn.
			</p>
			{playersWithName.length > 0 && (
				<Table>
					<thead>
						<TrHead>
							<Th>Name</Th>
							<Th>Number</Th>
							<Th>Id</Th>
						</TrHead>
					</thead>
					<tbody>
						{playersWithName
							.filter((p) => p.name && p.name.trim().length > 0)
							.map((player) => {
								return (
									<TrBody>
										<Td>{player.name}</Td>
										<Td>{player.color}</Td>
										<Td>{player.id}</Td>
									</TrBody>
								);
							})}
					</tbody>
				</Table>
			)}
			{!playersWithName.length && <small class="text-gray-700">Er zijn nog geen spelers.</small>}
			<div class="mt-5"></div>
			<form
				class="flex flex-col"
				on:submit={(e: SubmitEvent) => {
					e.preventDefault();
					const form = e.target as HTMLFormElement;
					const data = new FormData(form);
					const numberOfPlayers = Number(data.get('numberOfPlayers') ?? playersWithName.length);
					const numberOfQuestions = Number(data.get('numberOfQuestions') ?? state.numberOfQuestions);
					onContinue(numberOfPlayers, numberOfQuestions);
				}}
			>
				<div class="mb-3">
					<label class="block font-bold">Aantal spelers:</label>
					<input
						name="numberOfPlayers"
						type="number"
						min="2"
						max="50"
						value={currentNumberOfPlayers.toString()}
						step="1"
						class="mb-2 w-28 rounded-md border border-gray-300 bg-gray-50 p-1"
						on:input={(e: InputEvent) => {
							const value = (e.target as HTMLInputElement).value;
							const parsed = Number(value);
							if (!Number.isNaN(parsed)) {
								state.numberOfPlayers = parsed;
							}
						}}
					/>
					<Button
						type="button"
						on:click={() => {
							const playersCount =
								typeof state.numberOfPlayers === 'number' && state.numberOfPlayers > 0
									? state.numberOfPlayers
									: currentNumberOfPlayers;
							onSyncNumberOfPlayers(playersCount);
						}}
					>
						Bevestig aantal spelers
					</Button>
				</div>
				<div class="mb-3">
					<label class="block font-bold">Aantal vragen: {currentNumberOfQuestions.toString()}</label>
				</div>
				<Button type="submit" disabled={playersWithName.length === 0}>
					Doorgaan
				</Button>
			</form>
		</div>
	);
}

function PlayerTable({ state }: { state: PlayAndConnectState }) {
	return (
		<Table>
			<thead>
				<TrHead>
					<Th>Name</Th>
					<Th>Number</Th>
					<Th>Points</Th>
					<Th>id</Th>
				</TrHead>
			</thead>
			<tbody>
				{state.players
					.filter((p) => p.name && p.name.trim().length > 0)
					.map((player) => {
						return (
							<TrBody>
								<Td>{player.name}</Td>
								<Td>{player.color}</Td>
								<Td>{player.score}</Td>
								<Td>{player.id}</Td>
							</TrBody>
						);
					})}
			</tbody>
		</Table>
	);
}
